import React, { useState } from "react";
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { getAuth, GoogleAuthProvider, signInWithCredential, signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import keys from '../../utils/keys';

const isDev = window.location.hostname.includes("dev.");
const clientId = isDev ? keys.google.dev : keys.google.prod;

const AdminLogin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [resetSent, setResetSent] = useState(false);

  // ── Google sign-in ──────────────────────────────────────────────────────────
  const GoogleLoginComponent = () => {
    const handleLoginSuccess = async (response) => {
      setLoading(true);
      setError('');
      try {
        const accessToken = response['access_token'];
        const credential = GoogleAuthProvider.credential(null, accessToken);
        await signInWithCredential(getAuth(), credential);
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    };

    const handleLoginFailure = (err) => {
      setError(err.message);
    };

    const login = useGoogleLogin({
      onSuccess: handleLoginSuccess,
      onError: handleLoginFailure
    });

    return (
      <button
        type="button"
        className="flex justify-center gap-3 items-center h-[59px] lg:h-[80px] rounded-full border border-blue-default w-full shadow-shadow bg-white transform transition-transform duration-300 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
        onClick={() => login()}
        disabled={loading}
      >
        <span className="text-md lg:text-xl text-[inherit]">Sign in with Google</span>
      </button>
    );
  };

  // ── Email / password sign-in ────────────────────────────────────────────────
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await signInWithEmailAndPassword(getAuth(), email, password);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  // ── Forgot password ─────────────────────────────────────────────────────────
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await sendPasswordResetEmail(getAuth(), email);
      setResetSent(true);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const goToForgot = () => {
    setMode('forgot');
    setError('');
    setResetSent(false);
  };

  const goToLogin = () => {
    setMode('login');
    setError('');
    setResetSent(false);
  };

  // ── Forgot-password view ────────────────────────────────────────────────────
  if (mode === 'forgot') {
    return (
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h1>
          <p className="text-gray-600">Enter your email and we'll send you a reset link</p>
        </div>

        {resetSent ? (
          <div className="p-4 rounded-md bg-green-100 text-green-700 text-center mb-4">
            Reset email sent! Check your inbox.
          </div>
        ) : (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Reset Email'}
            </button>
          </form>
        )}

        {error && (
          <div className="mt-4 p-3 rounded-md bg-red-100 text-red-700">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={goToLogin}
          className="mt-6 w-full text-center text-sm text-blue-500 hover:underline"
        >
          ← Back to login
        </button>
      </div>
    );
  }

  // ── Login view ──────────────────────────────────────────────────────────────
  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Login</h1>
        <p className="text-gray-600">Sign in to access the admin dashboard</p>
      </div>

      {/* Google */}
      <GoogleOAuthProvider clientId={clientId}>
        <GoogleLoginComponent />
      </GoogleOAuthProvider>

      {/* Divider */}
      <div className="flex items-center my-6">
        <div className="flex-1 border-t border-gray-300" />
        <span className="mx-3 text-sm text-gray-400">or</span>
        <div className="flex-1 border-t border-gray-300" />
      </div>

      {/* Email / password */}
      <form onSubmit={handleEmailLogin} className="space-y-4">
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <div className="text-right">
          <button
            type="button"
            onClick={goToForgot}
            className="text-sm text-blue-500 hover:underline"
          >
            Forgot password?
          </button>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50"
        >
          {loading ? 'Signing in...' : 'Sign in with Email'}
        </button>
      </form>

      {error && (
        <div className="mt-4 p-3 rounded-md bg-red-100 text-red-700">
          {error}
        </div>
      )}

      {loading && (
        <div className="mt-4 text-center text-gray-600">
          Loading...
        </div>
      )}
    </div>
  );
};

export default AdminLogin;
